import { useState, useCallback } from 'react';
import { generatePath } from 'react-router-dom';
import { Link, CheckBox, Spin } from '@1c/ui';
import { useNavigate } from 'src/utils/useNavigate';
import Select from 'src/components/ui/Select';
import Button from 'src/components/Button';
import Text from 'src/components/Text';
import RouterLink from 'src/components/Link';
import LinkButton from 'src/components/LinkButton';
import { toast } from 'react-toastify';
import CertificateDetails from '../CertificateDetails';
import useWithAuthorities from 'src/utils/useWithAuthorities';
import permissions from 'src/constants/permissions';
import {
  getDataToSignByPluginAPI,
  postJoinBySignatureWithPluginAPI,
} from 'src/serverApi/joiningWithSignature';
import styles from './styles.module.scss';
import { useCades } from 'src/utils/cades';
import routes from 'src/constants/routes';
import CertOptionLabel from './CertOptionLabel';

const renderCertOptionLabel = option => <CertOptionLabel certificate={option} />;

const getCertOptionValue = ({ subjectName }) => subjectName;

const ApproveByPlugin = () => {
  const navigate = useNavigate();
  const [isCadesPluginOK, findCerts, isFindingCerts, certs, signData] = useCades();
  const [selectedCert, setSelectedCert] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [offerAgreementChecked, setOfferAgreementChecked] = useState(false);
  const withDataManagementAuthorities = useWithAuthorities([permissions.dataManagement]);

  const handleOfferAgreementClick = value => {
    setOfferAgreementChecked(value?.currentTarget?.checked);
  };

  const approveByPlugin = useCallback(
    async event => {
      event.preventDefault();

      setIsApproving(true);

      try {
        await withDataManagementAuthorities(async () => {
          const dataToSignByPlugin = await getDataToSignByPluginAPI();
          const signedData = await signData(selectedCert, dataToSignByPlugin);
          const data = await postJoinBySignatureWithPluginAPI(signedData);
          toast.success(
            'Проверка сертификата и ЭП выполнена успешно! Право работать с данными организации подтверждено!'
          );
          const url = data?.organizationUeid
            ? generatePath(routes.organization, { organizationUeid: data?.organizationUeid })
            : routes.agent;
          navigate(url);
        });
      } catch (error) {
        if (error.status === 422 && error.message) {
          toast.error(<div style={{ whiteSpace: 'pre-wrap' }}>{error?.message}</div>);
        } else {
          toast.error('При проверке сертификата и ЭП произошла ошибка.');
        }
      } finally {
        setIsApproving(false);
      }
    },
    [withDataManagementAuthorities, signData, selectedCert, navigate]
  );

  if (!isCadesPluginOK) {
    return (
      <Text type="p">
        На текущем рабочем месте установите, пожалуйста,{' '}
        <Link to="https://www.cryptopro.ru/products/cades/plugin" target="_blank">
          плагин
        </Link>
        .
      </Text>
    );
  }

  return (
    <>
      <Text type="p">
        Выберите сертификат ЭП, выписанный на указанную организацию.
        <br />
      </Text>
      {certs ? (
        <div className={styles['sign-by-plugin']}>
          <div className={styles['cert-list']}>
            <Select
              placeholder="Выберите сертификат"
              options={certs}
              value={selectedCert}
              onChange={setSelectedCert}
              formatOptionLabel={renderCertOptionLabel}
              getOptionValue={getCertOptionValue} // Эту функцию компонент будет использовать для сравнения значений между собой. Без неё, когда выбрана любая опция, то в списке подсвечивается не только она, а вообще все, потому что по умолчанию он сравнивает значения как строки и они все [object Object]
            />
          </div>
          {selectedCert && <CertificateDetails certificate={selectedCert} />}
          {selectedCert && (
            <>
              <Button
                onClick={approveByPlugin}
                disabled={isApproving || !offerAgreementChecked}
                className={styles['submit-button']}
              >
                Подтвердить
              </Button>
              <div className={styles['checkbox-wrapper']}>
                <CheckBox
                  className={styles['checkbox']}
                  checked={offerAgreementChecked}
                  onChange={handleOfferAgreementClick}
                >
                  Нажимая кнопку "Подтвердить", я принимаю условия{' '}
                </CheckBox>
                <RouterLink to={routes.offer} target="_blank">
                  договора оферты
                </RouterLink>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <LinkButton onClick={findCerts} className={styles['link']} disabled={isFindingCerts}>
            Показать доступные сертификаты
          </LinkButton>
          {isFindingCerts && (
            <>
              {' '}
              <Spin size="15px" className={styles['spin']} />
            </>
          )}
        </>
      )}
    </>
  );
};

export default ApproveByPlugin;
